<template>
  <div>
    <b-row>
      <b-col style="border-right: 1px solid #e2e8f0;" lg='7'>
        <!-- Content URL -->
        <div role="url">
          <label for="input-url">URL (optional):</label>
          <b-row>
            <b-col cols="9">
              <b-form-input
                id="input-url"
                v-model="form.url"
                :state="urlState"
                aria-describedby="input-url-help input-url-feedback"
                placeholder="URL of your content"
                :disabled="$apollo.queries.prefetchedContentShare.loading"
                trim
              ></b-form-input>
            </b-col>
            <b-col cols="3" class="d-flex justify-content-end align-items-end">
              <b-button
                variant="primary"
                @click="fetchContent"
                :disabled="$apollo.queries.prefetchedContentShare.loading || !urlState"
              >
                Fetch Details
              </b-button>
            </b-col>
          </b-row>

          <b-form-invalid-feedback id="input-live-feedback">
          Enter a valid URL
          </b-form-invalid-feedback>

          <b-form-text id="input-live-help">The URL of the content you want to be imported onto Real Links, it can be a <b>YouTube</b> or <b>Vimeo</b> link as well.</b-form-text>
        </div>

        <!-- Content Title -->
        <div class="mt-3" role="title">
          <label for="input-title">Title:</label>
          <b-form-input
            id="input-title"
            v-model="form.title"
            :state="titleState"
            aria-describedby="input-title-help input-title-feedback"
            placeholder="Title of your content"
            :disabled="$apollo.queries.prefetchedContentShare.loading"
            trim
          ></b-form-input>

          <b-form-invalid-feedback id="input-title-feedback">
          Enter at least 3 letters
          </b-form-invalid-feedback>
        </div>

        <hr class="my-4" />

        <!-- Content -->
        <div class="mt-3" role="title">
          <label for="input-title">Content:</label>
          <rich-text-field
            v-if="!$apollo.queries.prefetchedContentShare.loading"
            :placeholder="$t('content-fetch.placeholders.description')"
            v-model="form.content"
          />
          <b-textarea
            v-else
            :disabled="true"
            placeholder="Loading content..."
          />

          <b-form-invalid-feedback id="input-title-feedback">
          Enter at least 10 words or provide a YouTube/Vimeo link in the URL field.
          </b-form-invalid-feedback>
        </div>

        <hr class="my-4" />

        <!-- Image -->
        <div class="mt-3" role="image">
          <label for="input-image">Image:</label>
          <b-row class="py-0">
            <b-col lg="7" class="py-0">
              <b-form-input
                id="input-image"
                v-model="form.imageURL"
                placeholder="URL of the image"
                accept="image/*"
                :disabled="$apollo.queries.prefetchedContentShare.loading"
                trim
              ></b-form-input>
            </b-col>
            <b-col lg="1" style="text-align: center; line-height: 40px;">OR</b-col>
            <b-col lg="4" class="pt-1">
              <asset-block
                id="input-image-asset"
                class="pb-0 mb-0"
                :field="{ type: 'IMAGE' }"
                v-model="form.image"
                :disabled="$apollo.queries.prefetchedContentShare.loading"
              />
            </b-col>
          </b-row>
          <b-row class="py-0">
            <b-col class="py-0">
              <b-form-text id="input-image-help">The image that will be displayed with your content on the social media platforms. Add a link to the image or upload a file.</b-form-text>
            </b-col>
          </b-row>
        </div>

        <!-- Short description -->
        <div class="mt-3" role="description">
          <label for="input-description">Short description:</label>
          <b-form-textarea
            id="input-description"
            v-model="form.description"
            :state="descriptionState"
            :disabled="$apollo.queries.prefetchedContentShare.loading"
            aria-describedby="input-description-help"
            placeholder="A short description of your content"
            rows="3"
          ></b-form-textarea>

          <b-form-invalid-feedback id="input-description-feedback">
            Enter at least 10 words.
          </b-form-invalid-feedback>

          <b-form-text id="input-description-help">A short description of your content that will be displayed on social media platforms.</b-form-text>
        </div>

        <hr class="my-4" />

        <!-- Example share thext -->
        <div class="mt-3" role="content-share-text">
          <label for="input-content-share-text">Example share text:</label>
          <b-form-textarea
            id="input-content-share-text"
            v-model="form.customFields.contentShareText"
            :state="contentShareTextState"
            :disabled="$apollo.queries.prefetchedContentShare.loading"
            aria-describedby="input-content-share-text-help"
            placeholder="Example share text"
            trim
          ></b-form-textarea>

          <b-form-invalid-feedback id="input-description-feedback">
            Enter at least 5 words.
          </b-form-invalid-feedback>

          <b-form-text id="input-content-share-text-help">The text that will be suggested to your employees to include when they share on social media.</b-form-text>
        </div>

        <hr class="my-4" />

        <!-- Job Selector -->
        <div>
          <b-card>
            <b-form-group label="Related Jobs" v-slot="{ ariaDescribedby }">
              <b-form-radio v-model="form.relatedJobs" :aria-describedby="ariaDescribedby" name="some-radios" value="NO_JOBS">No jobs</b-form-radio>
              <b-form-radio v-model="form.relatedJobs" :aria-describedby="ariaDescribedby" name="some-radios" value="CAMPAIGN_JOBS">Jobs from current campaigns</b-form-radio>
              <b-form-radio v-model="form.relatedJobs" :aria-describedby="ariaDescribedby" name="some-radios" value="ALL_JOBS">All active jobs</b-form-radio>
              <b-form-radio v-model="form.relatedJobs" :aria-describedby="ariaDescribedby" name="some-radios" value="SEGMENTED_JOBS">Jobs matching a selected criteria</b-form-radio>
            </b-form-group>

            <b-button @click="$bvModal.show('segmented-jobs-modal')" variant="primary" v-if="form.relatedJobs === 'SEGMENTED_JOBS'">
              Select criteria for related jobs
            </b-button>

            <b-form-text id="input-content-share-text-help">Select what jobs you would like to display underneath your content.</b-form-text>
          </b-card>
        </div>

      </b-col>
      <b-col lg='5'>

        <!-- Preview -->
        <social-media-preview
          :title="form.title"
          :url="form.url"
          :description="form.description"
          :disabled="$apollo.queries.prefetchedContentShare.loading"
          :imageURL="form.imageURL || (form.image ? form.image.fileLocation : '')"
          :shareText="form.customFields.contentShareText"
          :loading="$apollo.queries.prefetchedContentShare.loading"
          />
        <p class="mt-3 text-muted"><small>*The illustration above is an example preview of how the post can look like on LinkedIn or on other social media channels.</small></p>
      </b-col>
    </b-row>
    <b-row class="mt-3 pt-3 border-top">
      <b-col class="py-0 d-flex justify-content-end align-items-end">
        <b-button
          variant="primary"
          @click="$emit('input', {
            ...form,
            url: form.url || null,
              customFields: {
              ...form.customFields,
            },
            image: form.image ? { id: form.image.id } : null,
            relatedJobSegment: form.relatedJobSegment ? JSON.stringify(form.relatedJobSegment) : null
          })"
          :disabled="$apollo.queries.prefetchedContentShare.loading || !titleState || !descriptionState || !contentShareTextState"
        >
          Save Content
        </b-button>
      </b-col>
    </b-row>

    <!-- Display modal if SEGMENTED_JOBS option is selected -->
    <b-modal id="segmented-jobs-modal" title="Select criteria for related jobs" hide-footer size="xl">
      <update-job-segment
        :value="form.relatedJobSegment"
        @saveSegment="saveSegment"
        modal-layout
      />
    </b-modal>
  </div>
</template>

<script>
import AssetBlock from '@/components/settings/AssetBlock';
import SocialMediaPreview from '@/components/content-share/content-share-editor/SocialMediaPreview';
import PrefetchContentShareQuery from '@/graphql/queries/PrefetchContentShare.gql';
import RichTextField from '@/components/RichTextField';
import UpdateJobSegment from '@/components/campaign-admin/update-job-segment';
import { mapRoot } from '@src/mixins/root';

export default {
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    AssetBlock,
    SocialMediaPreview,
    RichTextField,
    UpdateJobSegment,
  },
  data() {
    const { value } = this;
    const { customFields } = value || {};
    const { contentShareText } = customFields || {};
    return {
      form: {
        title: value.title || '',
        description: value.description || '',
        url: value.url || '',
        image: value.image || null,
        imageURL: value.imageURL || null,
        content: value.content || '',
        relatedJobSegment: value.relatedJobSegment && value.relatedJobSegment.length ? JSON.parse(value.relatedJobSegment) : null,
        relatedJobs: value.relatedJobs || 'CAMPAIGN_JOBS',
        customFields: {
          contentShareText: contentShareText || '🚀 Excited to share this content! 🌟',
        },
      },
      prefetchError: null,
    };
  },
  apollo: {
    prefetchedContentShare: {
      query: PrefetchContentShareQuery,
      variables() {
        const { form } = this;
        return { url: form.url };
      },
      manual: true,
      result({ data: { company }, loading }) {
        const { form } = this;
        if (!loading) {
          const { contentSharePrefetch } = company || {};
          const { image, ...contentShare } = contentSharePrefetch || {};
          console.log(contentShare);
          this.form = {
            ...form,
            imageURL: image || form.imageURL || null,
            title: contentShare.title || form.title || '',
            description: contentShare.description || form.description || '',
            content: contentShare.content || form.content || '',
          };
        }
      },
      error(error) {
        this.prefetchError = error;
      },
      skip() {
        const { isRecruiter, urlState, form, isEdit } = this;
        return !isRecruiter || !urlState || !form.url || isEdit;
      },
      fetchPolicy: 'network-only',
    },
  },
  watch: {
    'form.relatedJobs': {
      handler() {
        const { relatedJobs } = this.form;
        if (relatedJobs === 'SEGMENTED_JOBS') {
          this.$bvModal.show('segmented-jobs-modal');
        }
      },
      immediate: false,
      deep: true
    },
  },
  computed: {
    ...mapRoot([ 'isRecruiter' ]),
    urlState() {
      if (this.form.url.length > 0) {
        try {
          const url = new URL(this.form.url);
          if (url.protocol === 'http:' || url.protocol === 'https:') {
            return true;
          }
        } catch (e) {
          return false;
        }
      }
      return null;
    },
    titleState() {
      if (this.form.title.length >= 3) {
        return true;
      } else if (this.form.title.length > 0) {
        return false;
      }
      return null;
    },
    descriptionState() {
      if (this.form.description.split(/\s+/).length >= 10) {
        return true;
      } else if (this.form.description.length > 0) {
        return false;
      }
      return null;
    },
    contentShareTextState() {
      if (this.form.customFields.contentShareText.split(/\s+/).length >= 5) {
        return true;
      } else if (this.form.customFields.contentShareText.length > 0) {
        return false;
      }
      return null;
    },
  },
  methods: {
    async fetchContent() {
      if (this.urlState) {
        await this.$apollo.queries.prefetchedContentShare.refetch();
      }
    },
    saveSegment(segment) {
      this.form.relatedJobSegment = segment;
      this.$bvModal.hide('segmented-jobs-modal');
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/content-share/content-share-editor/AddContentForm.vue